import React,{useState,useEffect} from "react";
import { refreshNotifications, journalHasMore, JOURNAL_PAGE_SIZE } from "../utils/notifications";
import { formatUsPhone, findContactByNumber, contactDisplayName } from "../utils/contacts";
import { formatSeconds } from "../utils/format";
import ListParty from "./listParty";
import WhenText from "./whenText";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Grid, Button } from "@mui/material";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallMissedIcon from "@mui/icons-material/CallMissed";
import CallMissedOutgoingIcon from "@mui/icons-material/CallMissedOutgoing";
import { withRouter } from "../utils/router";
import EnhancedTable from "./enhancedTable";
import {
  getCallJournalPage,
  updateCallJournal,
} from "../utils/utils";
import {
  setNotifications,
  setNotificationsLoading,
} from "../store/features/notification/notificationSlice";
import { setCalls } from "../store/features/calls/callSlice";
import { setVoicemails } from "../store/features/voicemails/voicemailSlice";
import { setSmsConversations } from "../store/features/smsConversations/smsConversationsSlice";
import { setFaxes } from "../store/features/faxes/faxSlice";
import { setRecordings } from "../store/features/recordings/recordingSlice";
import { setError } from "../store/features/error/errorSlice";
import { resetSession } from "../store/features/session/sessionSlice";

const mapDispatchToProps = {
  setCalls,
  setVoicemails,
  setSmsConversations,
  setFaxes,
  setRecordings,
  setNotifications,
  setError,
  setNotificationsLoading,
  resetSession,
};

const headCells = {
  all: [
    {
      id: "from",
      numeric: false,
      disablePadding: false,
      label: "Contact",
    },
    { id: "duration", numeric: true, disablePadding: false, label: "Duration" },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "received", numeric: true, disablePadding: false, label: "Date" },
    { id: "actions", numeric: true, disablePadding: false, label: "Actions" },
  ],
  missed: [
    {
      id: "from",
      numeric: false,
      disablePadding: false,
      label: "From",
    },
    { id: "duration", numeric: true, disablePadding: false, label: "Duration" },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "received", numeric: true, disablePadding: false, label: "Date" },
    { id: "actions", numeric: true, disablePadding: false, label: "Actions" },
  ],
  incoming: [
    {
      id: "from",
      numeric: false,
      disablePadding: false,
      label: "From",
    },
    { id: "duration", numeric: true, disablePadding: false, label: "Duration" },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "received", numeric: true, disablePadding: false, label: "Date" },
    { id: "actions", numeric: true, disablePadding: false, label: "Actions" },
  ],
  outgoing: [
    {
      id: "to",
      numeric: false,
      disablePadding: false,
      label: "To",
    },
    { id: "duration", numeric: true, disablePadding: false, label: "Duration" },
    { id: "status", numeric: true, disablePadding: false, label: "Status" },
    { id: "received", numeric: true, disablePadding: false, label: "Date" },
    { id: "actions", numeric: true, disablePadding: false, label: "Actions" },
  ],
};

const CallJournal = (props) => {
  const {
    title,
    menu,
    menuItems,
    history,
    calls: storedCalls,
    personalPhonebook,
    session,
    resetSession,
    setCalls,
    setVoicemails,
    setSmsConversations,
    setFaxes,
    setRecordings,
    setNotifications,
    setNotificationsLoading,
    handleDialClick,
    setCalling,
    setValue,
    match,
    notifications,
    setError,
  } = props;

  let journalTab = match.params.journalTab;
  const [page,setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  // Older calls, loaded a page at a time with "Load more". The newest page
  // lives in the store and is reloaded by the rest of the app; these are
  // kept here and shown after it.
  const [olderCalls, setOlderCalls] = useState([]);
  const [olderPages, setOlderPages] = useState(0);
  // null: not asked yet, so the newest page says whether there is more.
  const [moreOlder, setMoreOlder] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const defaultValues = {
    server: session.lastServer,
    token: session.token,
  };

  // Everything loaded so far, newest first, each call once.
  const newestIds = new Set(storedCalls.map((item) => item.jid));
  const calls = [...storedCalls, ...olderCalls.filter((item) => !newestIds.has(item.jid))];
  const hasMore = moreOlder === null ? journalHasMore() : moreOlder;

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const res = await getCallJournalPage(
      { server: session.lastServer, token: session.token },
      JOURNAL_PAGE_SIZE * (olderPages + 1),
      JOURNAL_PAGE_SIZE
    );
    setLoadingMore(false);
    if (res instanceof Error) {
      if (res.message === "Unauthorized") {
        resetSession();
        history.push('/login');
        return;
      }
      setError({ visible: true, type: "error", message: "Older calls could not be loaded. " + res.message });
      return;
    }
    const entries = Array.isArray(res.journalEntries) ? res.journalEntries : [];
    const known = new Set(calls.map((item) => item.jid));
    const added = entries.filter((item) => !known.has(item.jid));
    setOlderCalls([...olderCalls, ...added]);
    setOlderPages(olderPages + 1);
    // The server says whether there is more. An older server does not: then
    // a full page with something new in it means there may be.
    setMoreOlder(res.paging && typeof res.paging.hasMore === "boolean"
      ? res.paging.hasMore
      : entries.length >= JOURNAL_PAGE_SIZE && added.length > 0);
  };

  const isUnanswered = (item) => String(item.state || "").toLowerCase().replace(/\s/g, "") !== "answered";

  // Who the call was with. The name is the one the server sent, otherwise a
  // personal contact whose number matches. Under it: the number and the kind
  // of call. The icon shows the direction, in red for a missed call.
  const callName = (item) => {
    const serverName = item.hasOwnProperty("name") ? String(item.name || "").trim() : "";
    return serverName !== ""
      ? serverName
      : contactDisplayName(findContactByNumber([personalPhonebook], item.number));
  };

  const getFromValue = (item) => {
    const name = callName(item);
    const number = String(formatUsPhone(item.number) || "");

    const outgoing = item.direction === "outgoing";
    const unanswered = isUnanswered(item);
    const missed = !outgoing && unanswered;
    const kind = missed ? "Missed call" : outgoing ? (unanswered ? "Outgoing, not answered" : "Outgoing") : "Incoming";
    const Icon = missed ? CallMissedIcon : outgoing ? (unanswered ? CallMissedOutgoingIcon : CallMadeIcon) : CallReceivedIcon;
    const tone = missed ? "danger" : outgoing ? (unanswered ? "neutral" : "brand") : "success";

    return (
      <ListParty
        icon={<Icon />}
        iconLabel={kind}
        tone={tone}
        title={name !== "" ? name : number}
        detail={name !== "" && number !== "" ? `${number} · ${kind}` : kind}
        detailDanger={missed}
        isNew={item.viewed === "no"}
      />
    );
  };

  // Search: a name, or a number typed any way ("561 877" finds 5618775080).
  const term = searchTerm.trim().toLowerCase();
  const termDigits = /^[\d\s()+.-]+$/.test(term) ? term.replace(/\D/g, "") : "";
  const matchesSearch = (item) => {
    if (term.length < 2) return true;
    if (callName(item).toLowerCase().includes(term)) return true;
    const digits = String(item.number || "").replace(/\D/g, "");
    if (String(item.number || "").toLowerCase().includes(term)) return true;
    if (termDigits.length < 2) return false;
    return digits.includes(termDigits) ||
      (termDigits.length > 2 && termDigits[0] === "1" && digits.includes(termDigits.slice(1)));
  };

  const inTab = (item) => {
    switch (journalTab) {
      case "missed":
        return item.state.toLowerCase() === "no answer" && item.direction === "incoming";
      case "incoming":
        return item.direction === "incoming";
      case "outgoing":
        return item.direction === "outgoing";
      case "all":
        return true;
      default:
        return false;
    }
  };

  const generateRows = ()=>{
    // The first column is "To" in the outgoing tab and "From" elsewhere.
    const partyKey = journalTab === "outgoing" ? "to" : "from";
    return calls
      .filter((item) => inTab(item) && matchesSearch(item))
      .map((item) => ({
        [partyKey]: getFromValue(item),
        duration: formatSeconds(item.duration),
        status: item.state,
        received: <WhenText dateTime={item.dateTime} />,
        actions: [{ type: "call" }, { type: "deleteCall" }],
        object: item,
      }));
  }

  const refetchNotifications = () => refreshNotifications({
    values: defaultValues,
    setters: { setCalls, setFaxes, setRecordings, setVoicemails, setSmsConversations },
    setNotifications,
    setNotificationsLoading,
    // This page shows or changes the call journal: always the server's copy.
    freshJournal: true,
    onUnauthorized: () => {
      resetSession();
      history.push('/login');
    },
  });

  const clearCurrentNotifications = () =>{
    let values = {
      server: session.lastServer,
      token: session.token,
    };

    // eslint-disable-next-line
    values.call = calls.map((item) => {
      if (item.viewed.toLowerCase() === "no") {
        return {
          jid: item.jid,
          viewed: "yes",
          type: item.type,
          direction: item.direction,
        };
      }
    }).filter(item=>typeof item!=="undefined");

    try {
      updateCallJournal(values);
      refetchNotifications();
    } catch (error) {
      console.error(error);
    }
  }

  const handleDeleteForever = (item) => {
    let values = defaultValues;
    values.call = [{
      jid: item.jid,
      viewed: item.viewed,
      type: "deleted",
      direction: item.direction,
    }];

    try {
      updateCallJournal(values);
      // An older call is kept here, not in the store: drop it here too.
      setOlderCalls(olderCalls.filter((call) => call.jid !== item.jid));
      setError({
        visible: true,
        type: "success",
        message: "Successfully deleted Item",
      });
      refetchNotifications();
    } catch (error) {
      setError({
        visible: true,
        type: "error",
        message: error,
      });
      console.error(error);
    }
  };

  useEffect(() => {
    clearCurrentNotifications();
    // eslint-disable-next-line
  }, [journalTab, title]);

  // Beside the paging: how much of the history is loaded, and more of it.
  const footerAction = (hasMore || olderPages > 0) ? (
    <>
      <span>
        {hasMore
          ? `Latest ${calls.length} calls loaded`
          : `All ${calls.length} calls loaded`}
      </span>
      {hasMore && (
        <Button
          size="small"
          variant="outlined"
          onClick={loadMore}
          disabled={loadingMore}
          sx={{ textTransform: "none" }}
        >
          {loadingMore ? "Loading…" : "Load more"}
        </Button>
      )}
    </>
  ) : null;

  return (
    <Grid container>
      <Grid size={12}>
        <EnhancedTable
          headerData={headCells}
          rowData={generateRows()}
          hasActions={true}
          selectable={false}
          menu={menu ? menuItems : false}
          searchFunc={(value) => { setSearchTerm(value); setPage(0); }}
          footerAction={footerAction}
          title={title}
          tab={journalTab}
          history={history}
          hasClear={false}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          page={page}
          handleDialClick={handleDialClick}
          setCalling={setCalling}
          setValue={setValue}
          loading={notifications.loading}
          handleDeleteForever={handleDeleteForever}
          useGroupFilter={false}
          refreshDataOnSameTab={clearCurrentNotifications}
        />
      </Grid>
    </Grid>
  );
};

CallJournal.propTypes = {
  title: PropTypes.string.isRequired,
  menu: PropTypes.bool.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      linkId: PropTypes.string,
    })
  ),
  tab: PropTypes.string.isRequired,
  handleDialClick: PropTypes.func.isRequired,
  setCalling: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired
};

const mapStateToProps = (state) => {
  return {
    session: state.session,
    calls: state.calls,
    notifications: state.notifications,
    personalPhonebook: state.personalPhonebook,
  };
};

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(CallJournal));